/**
 * The group footer (spec M1-B4 §8.10, `[B2§8.4]` — layout ASSUMED, Q148).
 *
 * A 36 px row under the add-item row, one cell per visible column aligned to the same
 * `left`/`width` as the body cells, rendering the column type's `Summary`; the name column
 * shows nothing and `settings.hide_footer` suppresses one column's cell `[B5§5.3 F7]`.
 */
import type { ColumnId } from '@elizmonday/shared';
import { columnWidth } from '../columns/registry';
import type { BoardColumn, BoardGroupView } from '../data/types';
import { CHECKBOX_GUTTER, GROUP_BAR_WIDTH } from './layout';
import { SummaryCell } from './SummaryCell';

export interface GroupFooterProps {
  group: BoardGroupView;
  top: number;
  height: number;
  pinned: readonly BoardColumn[];
  scrolling: readonly BoardColumn[];
  layout: readonly { id: string; left: number; width: number }[];
  columnRange: { start: number; end: number };
  nameWidth: number;
  stickyWidth: number;
  scrollWidth: number;
  onOpenSummaryMenu(columnId: ColumnId, x: number, y: number): void;
}

export function GroupFooter({
  group,
  top,
  height,
  pinned,
  scrolling,
  layout,
  columnRange,
  nameWidth,
  stickyWidth,
  scrollWidth,
  onOpenSummaryMenu,
}: GroupFooterProps) {
  const extraPinned = pinned.filter((column) => column.id !== 'name');

  const cell = (column: BoardColumn, style: React.CSSProperties) => (
    <SummaryCell
      key={column.id}
      group={group}
      column={column}
      style={style}
      onOpenSummaryMenu={onOpenSummaryMenu}
    />
  );

  return (
    <div
      className="ez-footer"
      role="row"
      data-testid={`group-footer-${group.id}`}
      style={{ top, height, width: stickyWidth + scrollWidth }}
    >
      <div className="ez-footer__sticky" role="presentation" style={{ width: stickyWidth }}>
        <span className="ez-row__bar" style={{ width: GROUP_BAR_WIDTH }} aria-hidden="true" />
        <span
          className="ez-row__gutter"
          role="gridcell"
          style={{ width: CHECKBOX_GUTTER }}
          aria-hidden="true"
        />
        {/* The name column's footer is empty `[B2§8.4]`. */}
        <span className="ez-footer__name" role="gridcell" style={{ width: nameWidth }} aria-hidden="true" />
        <span className="ez-footer__chat" role="gridcell" aria-hidden="true" />
        {extraPinned.map((column) => cell(column, { width: columnWidth(column) }))}
      </div>
      <div className="ez-footer__scroll" role="presentation" style={{ width: scrollWidth }}>
        {scrolling.slice(columnRange.start, columnRange.end).map((column, offset) => {
          const box = layout[columnRange.start + offset];
          if (!box) return null;
          return cell(column, { position: 'absolute', left: box.left, width: box.width });
        })}
      </div>
    </div>
  );
}
